/**
 * IngressHostPrefixPicker - campo do Scaffolder para dominios IBICT.
 *
 * O usuario digita apenas o prefixo do host. Por padrao, o sufixo exibido
 * depende do cluster selecionado no form:
 *
 * - k8s-dev-01 -> .dev.ibict.br
 * - demais clusters -> .prod.ibict.br
 *
 * Para templates que sempre geram mais de um ambiente, e possivel passar:
 *
 * ui:options:
 *   environments:
 *     - label: DEV
 *       cluster: k8s-dev-01
 *       suffix: .dev.ibict.br
 *     - label: PRD
 *       cluster: k8s-prd-01
 *       suffix: .prod.ibict.br
 *
 * O valor enviado ao template continua sendo apenas o prefixo; o template
 * monta o FQDN final para manter a regra tambem no backend.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  InputAdornment,
  TextField,
} from '@material-ui/core';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import type {
  CustomFieldValidator,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

const suffixForCluster = (cluster: string | undefined): string => {
  if (cluster === 'k8s-dev-01') return '.dev.ibict.br';
  if (cluster) return '.prod.ibict.br';
  return '.<cluster>.ibict.br';
};

export type EnvironmentPreview = {
  label: string;
  cluster: string;
  suffix: string;
  hostPrefixes: string[];
};

type DomainCheckResult = {
  cluster: string;
  hostname: string;
  available: boolean;
  conflict?: { type: 'ingress' | 'claim' };
};

const DOMAIN_CHECK_ENDPOINT = '/api/ibict/domains/check';
const DOMAIN_AVAILABILITY_MESSAGES = [
  'Dominio ja utilizado:',
  'Nao foi possivel verificar a disponibilidade do dominio',
  'Verificando disponibilidade do dominio',
];

export function isDomainAvailabilityError(error: unknown): boolean {
  const message =
    typeof error === 'string'
      ? error
      : String(
          (error as { message?: unknown; stack?: unknown })?.message ??
            (error as { stack?: unknown })?.stack ??
            '',
        );
  return DOMAIN_AVAILABILITY_MESSAGES.some(marker => message.includes(marker));
}

export const getEnvironmentPreviews = (
  uiSchema: FieldExtensionComponentProps<string>['uiSchema'],
): EnvironmentPreview[] => {
  const environments = (uiSchema as any)?.['ui:options']?.environments;

  if (!Array.isArray(environments)) {
    return [];
  }

  return environments
    .map(environment => ({
      label: String(environment?.label ?? '').trim(),
      cluster: String(environment?.cluster ?? '').trim(),
      suffix: String(environment?.suffix ?? '').trim(),
      hostPrefixes: Array.isArray(environment?.hostPrefixes)
        ? environment.hostPrefixes.map((prefix: unknown) => String(prefix))
        : [''],
    }))
    .filter(
      environment =>
        environment.label && environment.cluster && environment.suffix,
    );
};

export function buildDomainTargets(
  prefixValue: string | undefined,
  environments: EnvironmentPreview[],
  cluster: string | undefined,
) {
  const prefix = (prefixValue ?? '').trim().toLowerCase();
  if (!prefix) return [];
  if (environments.length > 0) {
    return environments.flatMap(environment =>
      environment.hostPrefixes.map(hostPrefix => ({
        cluster: environment.cluster,
        hostname: `${hostPrefix}${prefix}${environment.suffix}`,
      })),
    );
  }
  if (!cluster) return [];
  return [{ cluster, hostname: `${prefix}${suffixForCluster(cluster)}` }];
}

export function getRelevantRawErrors(
  rawErrors: readonly string[] | undefined,
  targets: Array<{ hostname: string }>,
): string[] {
  return (rawErrors ?? []).filter(error => {
    if (
      isDomainAvailabilityError(error) &&
      !error.startsWith('Dominio ja utilizado:')
    )
      return false;
    if (!error.startsWith('Dominio ja utilizado:')) return true;
    return targets.some(target => error.includes(target.hostname));
  });
}

export const validateIngressHostPrefix: CustomFieldValidator<string> = async (
  value,
  field,
  context,
) => {
  const clusters = (context.formData as any)?.clusters;
  const cluster = Array.isArray(clusters) ? clusters[0] : undefined;
  const environments = getEnvironmentPreviews(context.uiSchema as any);
  const targets = buildDomainTargets(value, environments, cluster);
  if (targets.length === 0) return;

  try {
    const fetchApi = context.apiHolder.get(fetchApiRef);
    if (!fetchApi) throw new Error('fetchApi indisponivel');
    const response = await fetchApi.fetch(DOMAIN_CHECK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targets }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { results: DomainCheckResult[] };
    const unavailable = (body.results ?? []).filter(
      result => !result.available,
    );
    if (unavailable.length > 0) {
      field.addError(
        `Dominio ja utilizado: ${unavailable
          .map(result => result.hostname)
          .join(', ')}`,
      );
    }
  } catch {
    field.addError('Nao foi possivel verificar a disponibilidade do dominio');
  }
};

const IngressHostPrefixPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    formData,
    rawErrors,
    required,
    schema,
    formContext,
    uiSchema,
  } = props;
  const fetchApi = useApi(fetchApiRef);

  const clusters = (formContext as any)?.formData?.clusters;
  const cluster = Array.isArray(clusters) ? clusters[0] : undefined;
  const environmentPreviews = useMemo(
    () => getEnvironmentPreviews(uiSchema),
    [uiSchema],
  );
  const suffix = suffixForCluster(cluster);
  const prefixPreview = formData || 'exemplo';
  const hostPreview = `${prefixPreview}${suffix}`;
  const hasEnvironmentPreviews = environmentPreviews.length > 0;
  const [checking, setChecking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(
    null,
  );
  const [results, setResults] = useState<DomainCheckResult[]>([]);

  const targets = useMemo(() => {
    return buildDomainTargets(formData, environmentPreviews, cluster);
  }, [cluster, environmentPreviews, formData]);
  const targetsKey = JSON.stringify(targets);
  const latestTargetsKey = useRef(targetsKey);
  latestTargetsKey.current = targetsKey;
  const relevantRawErrors = getRelevantRawErrors(rawErrors, targets);

  useEffect(() => {
    if (targets.length === 0) {
      setChecking(false);
      setAvailabilityError(null);
      setResults([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setChecking(true);
      setAvailabilityError(null);
      try {
        const response = await fetchApi.fetch(DOMAIN_CHECK_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targets }),
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = (await response.json()) as {
          results: DomainCheckResult[];
        };
        if (latestTargetsKey.current !== targetsKey) return;
        const nextResults = body.results ?? [];
        const unavailable = nextResults.filter(result => !result.available);
        setResults(nextResults);
        if (unavailable.length > 0) {
          const message = `Dominio ja utilizado: ${unavailable
            .map(result => result.hostname)
            .join(', ')}`;
          setAvailabilityError(message);
        }
      } catch (error: any) {
        if (error?.name === 'AbortError') return;
        if (latestTargetsKey.current !== targetsKey) return;
        const message =
          'Nao foi possivel verificar a disponibilidade do dominio';
        setAvailabilityError(message);
        setResults([]);
      } finally {
        if (
          !controller.signal.aborted &&
          latestTargetsKey.current === targetsKey
        ) {
          setChecking(false);
        }
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // O Stepper recria onChange, uiSchema e arrays derivados a cada render.
    // A chave serializada dispara nova consulta apenas quando os alvos mudam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchApi, targetsKey]);

  let helperText;
  if (availabilityError) {
    helperText = availabilityError;
  } else if (relevantRawErrors.length) {
    helperText = relevantRawErrors.join(', ');
  } else if (hasEnvironmentPreviews) {
    helperText = (
      <>
        {checking ? 'Verificando dominios: ' : 'Dominios gerados: '}
        {environmentPreviews
          .map(environment => {
            const environmentResults = results.filter(
              result => result.cluster === environment.cluster,
            );
            const status =
              environmentResults.length > 0 &&
              environmentResults.every(result => result.available)
                ? 'disponivel'
                : '';
            const hosts = environment.hostPrefixes
              .map(prefix => `${prefix}${prefixPreview}${environment.suffix}`)
              .join(', ');
            return `${environment.label}: ${hosts}${
              status ? ` (${status})` : ''
            }`;
          })
          .join(' | ')}
      </>
    );
  } else {
    helperText = `Host final: ${hostPreview}`;
  }

  const inputProps = hasEnvironmentPreviews
    ? undefined
    : {
        endAdornment: <InputAdornment position="end">{suffix}</InputAdornment>,
      };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={Boolean(availabilityError || relevantRawErrors.length)}
      fullWidth
    >
      <TextField
        label={(schema.title as string) ?? 'Dominio do site'}
        value={formData ?? ''}
        onChange={e => {
          setAvailabilityError(null);
          setResults([]);
          onChange(e.target.value);
        }}
        helperText={helperText}
        error={Boolean(availabilityError || relevantRawErrors.length)}
        variant="outlined"
        required={required}
        InputLabelProps={{ shrink: Boolean(formData) }}
        InputProps={{
          ...inputProps,
          endAdornment: checking ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            inputProps?.endAdornment
          ),
        }}
      />
    </FormControl>
  );
};

export const IngressHostPrefixPickerField = createFormField({
  name: 'IngressHostPrefixPicker',
  component: IngressHostPrefixPicker,
  validation: validateIngressHostPrefix,
});

export const ingressHostPrefixPickerField = FormFieldBlueprint.make({
  name: 'ingress-host-prefix-picker',
  params: {
    field: async () => IngressHostPrefixPickerField,
  },
});
